import * as React from 'react'

import { Check, ChevronsUpDown, Loader } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'

import { useControllableState } from '@/lib/hooks/use-controllable-state'

import { cx } from '@/lib/cva-config'
import { FormControl } from '../form'

interface FormFieldSelectProps extends React.ComponentProps<typeof Button> {
  items: {
    label: string
    value: string
  }[]
  placeholder?: string
  onValueChange?: (value: string) => void
  value?: string
  isLoading?: boolean
  cmdPlaceholder?: string
  search?: string
  setSearch?: (search: string) => void
  fallbackMsg?: string
}

export function FormFieldSelectCombobox(props: FormFieldSelectProps) {
  const {
    items,
    value,
    onValueChange,
    placeholder,
    isLoading,
    cmdPlaceholder,
    search,
    setSearch,
    disabled,
  } = props

  const [_search, _setSearch] = useControllableState({
    defaultProp: '',
    onChange: setSearch,
    prop: search,
  })

  const renderedValue = React.useMemo(() => {
    let val = placeholder
    if (isLoading) return 'Loading...'
    if (value) {
      const found = items.find((item) => item.value === value)?.label
      if (found) val = found
    }
    return val
  }, [value, placeholder, items, isLoading])

  const memoizedLoader = React.useMemo(
    () => <Loader className="h-4 w-4 animate-spin text-muted-foreground" />,
    []
  )

  return (
    <Popover>
      <FormControl>
        <PopoverTrigger asChild>
          <Button variant="outline" role="combobox" disabled={disabled}>
            <span
              className={cx(
                'flex-1 text-left',
                (isLoading || renderedValue === placeholder) &&
                  'text-muted-foreground'
              )}
            >
              {renderedValue}
            </span>
            {isLoading ? (
              memoizedLoader
            ) : (
              <ChevronsUpDown className="opacity-50" />
            )}
          </Button>
        </PopoverTrigger>
      </FormControl>
      <PopoverContent className="p-0">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={cmdPlaceholder}
            value={_search}
            onValueChange={_setSearch}
          />
          <CommandList>
            {/* <CommandEmpty>{fallbackMsg}</CommandEmpty> */}
            <div
              className={cx(
                'px-4 py-2 text-xs',
                isLoading ? 'block' : 'hidden'
              )}
              aria-hidden="true"
            >
              Loading...
            </div>
            <CommandGroup className={cx(isLoading && 'hidden')}>
              {items.map((item) => (
                <CommandItem
                  key={item.value}
                  value={item.value}
                  onSelect={onValueChange}
                >
                  {item.label}
                  <Check
                    className={cx(
                      'ml-auto',
                      value === item.value ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
